// HeroStrip — the top hero section of the dashboard.
//
// Left side: "Total revenue" label + huge $-style number + tiny ±%
// chip + "Available to spend: …" + Transfer/Request pill buttons.
// Right side: time-range pill toggle (Week / Month / Year) above
// the bar visualisation.
//
// Mirrors the reference dashboard's top half. The bar visualisation
// itself comes from RevenueBars.tsx.

import { ReactNode, useState } from 'react';
import { motion } from 'motion/react';
import Icon from '../../../components/Icon';
import { useCountUp } from '../../lib/useCountUp';

type Props = {
  /** "Total revenue" / "Total platform earnings" — the main metric label. */
  label: string;
  /** Numeric headline value (will count up + render with smart decimals). */
  value: number;
  prefix?: string;
  /** Subtitle line under the headline. */
  subtitle?: ReactNode;
  /** Tiny coloured chip next to the value (e.g. "+12.67%"). */
  delta?: { value: number; positive?: boolean } | null;
  /** Right-side block (the live "Today snapshot" card or any other content). */
  right?: ReactNode;
  /** Show the Week/Month/Year range pill toggle in the right column.
      Defaults to false because we don't have a timeseries endpoint
      live yet — turning this on without backed data shows a fake
      affordance. Flip it on once we wire `range` through to a chart. */
  showRange?: boolean;
  range?: 'week' | 'month' | 'year';
  onRangeChange?: (r: 'week' | 'month' | 'year') => void;
  /** Action buttons under the headline (Transfer / Request etc.). */
  actions?: ReactNode;
};

export default function HeroStrip({
  label, value, prefix = '$', subtitle, delta, right,
  showRange = false, range = 'month', onRangeChange, actions,
}: Props) {
  const animated = useCountUp(value);
  const display = Math.round(animated);
  const [r, setR] = useState(range);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="grid grid-cols-1 lg:grid-cols-[minmax(280px,400px)_1fr] gap-6 lg:gap-10 px-2 lg:px-4 pt-4 lg:pt-8 pb-6"
    >
      {/* Left: headline */}
      <div className="min-w-0">
        <div className="text-[12.5px] text-neutral-500 dark:text-white/55 mb-1">{label}</div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="text-4xl sm:text-5xl lg:text-[56px] font-display font-extrabold text-neutral-900 dark:text-white tabular-nums leading-[0.95] tracking-tight">
            {prefix}
            <span>{display.toLocaleString('en-IN')}</span>
            <span className="text-neutral-400 dark:text-white/35 font-bold">.00</span>
          </div>
          {delta && (
            <span className={`
              text-[13px] font-bold tabular-nums
              ${delta.positive !== false ? 'text-emerald-600 dark:text-cyan-300' : 'text-rose-600 dark:text-rose-400'}
            `}>
              {delta.positive !== false ? '+' : ''}{delta.value.toFixed(2)}%
            </span>
          )}
        </div>
        {subtitle && <div className="text-[12.5px] text-neutral-500 dark:text-white/55 mt-3">{subtitle}</div>}
        {actions && <div className="flex items-center gap-2 mt-4 flex-wrap">{actions}</div>}
      </div>

      {/* Right: optional range toggle + content slot */}
      <div className="min-w-0">
        {showRange && (
          <div className="flex justify-end mb-4">
            <div className="inline-flex rounded-full bg-neutral-100 dark:bg-white/5 border border-neutral-200 dark:border-white/10 p-1 backdrop-blur-sm">
              {(['week','month','year'] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => { setR(opt); onRangeChange?.(opt); }}
                  className={`px-3.5 py-1 rounded-full text-[12px] font-medium capitalize transition
                              ${r === opt
                                ? 'bg-violet-600 text-white dark:bg-white dark:text-neutral-900 shadow-[0_2px_10px_rgba(139,92,246,0.35)] dark:shadow-[0_2px_10px_rgba(0,0,0,0.35)]'
                                : 'text-neutral-500 hover:text-neutral-900 dark:text-white/55 dark:hover:text-white'}`}
                >{opt}</button>
              ))}
            </div>
          </div>
        )}
        {right}
      </div>
    </motion.section>
  );
}

/* ─── Companion: rounded white pill button used for the actions row.
   Phase 3 polish: optional `tone` prop. Admin SPA gets the original
   violet ("admin"); agent SPA passes `tone="agent"` so the action
   pills land in agent's brand magenta. Default stays violet so any
   existing admin call sites continue to render unchanged. ─── */

export function ActionPill({
  label, icon, onClick, to, tone = 'admin',
}: {
  label: string; icon: string; onClick?: () => void; to?: string;
  tone?: 'admin' | 'agent';
}) {
  const inner = (
    <>
      {label}
      <Icon name={icon} size={14} />
    </>
  );
  const toneCls = tone === 'agent'
    ? 'bg-brand-500 text-white dark:bg-brand-500 dark:text-white shadow-[0_2px_12px_rgba(var(--brand-rgb),0.45)]'
    : 'bg-violet-600 text-white dark:bg-white dark:text-arena-900 shadow-[0_2px_12px_rgba(139,92,246,0.35)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.35)]';
  const cls = `inline-flex items-center gap-1.5 px-4 py-2 rounded-full
               ${toneCls}
               text-[13px] font-semibold
               hover:scale-[1.02] active:scale-[0.98] transition`;
  if (to) {
    return <a href={to} className={cls}>{inner}</a>;
  }
  return <button onClick={onClick} className={cls}>{inner}</button>;
}

export function CircleIcon({
  icon, onClick, title,
}: { icon: string; onClick?: () => void; title?: string }) {
  return (
    <button
      onClick={onClick} title={title}
      className="grid place-items-center w-10 h-10 rounded-full bg-neutral-100 hover:bg-neutral-200 dark:bg-black/40 dark:hover:bg-black/55
                 border border-neutral-200 dark:border-white/10 transition active:scale-95"
    >
      <Icon name={icon} size={16} className="text-neutral-700 dark:text-white/75" />
    </button>
  );
}
